import { Box, MenuItem, Select, Typography } from "@mui/material";
import { useMemo } from "react";
import { useLidarClearance } from "./LidarClearanceContext";
import { buildGradient } from "./colorScaleDisplay";

const NULL_VALUE = "__default__";

export function GradientSwatch({ gradient }: { gradient: string | undefined }) {
  return (
    <Box
      sx={{
        height: 8,
        flexGrow: 1,
        minWidth: 40,
        backgroundColor: "grey.300",
        backgroundImage: gradient,
      }}
    />
  );
}

function OptionRow({
  label,
  gradient,
  noWrap,
}: {
  label: string | number;
  gradient: string | undefined;
  noWrap?: boolean;
}) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
      <Typography variant="body2" noWrap={noWrap} sx={{ minWidth: 50 }}>
        {label}
      </Typography>
      <GradientSwatch gradient={gradient} />
    </Box>
  );
}

type DefaultOption = {
  label: string;
  // The scale id to preview when `value` is null - e.g. an axis's own
  // default color_scale, previewed under a "Default" entry.
  scaleId: number;
};

type Props = {
  region: string;
  // The value range to normalize each option's gradient against - the
  // axis's own min/max, since that's what this scale will actually render
  // against once picked.
  min: number;
  max: number;
  value: number | null;
  onChange: (value: number | null) => void;
  // Adds a "use the default" option that resolves to null - omit for a
  // plain required picker (e.g. an axis's own default color scale, which
  // has no higher-level fallback to fall back to).
  defaultOption?: DefaultOption;
};

export default function ColorScalePicker({
  region,
  min,
  max,
  value,
  onChange,
  defaultOption,
}: Props) {
  const { colorScales } = useLidarClearance();

  const scalesInRegion = useMemo(
    () => colorScales.filter((scale) => scale.region === region),
    [colorScales, region]
  );

  const resolvedScaleId = value ?? defaultOption?.scaleId;
  const selectedScale = scalesInRegion.find(
    (scale) => scale.color_scale_id === resolvedScaleId
  );
  const selectedGradient = selectedScale
    ? buildGradient(selectedScale.colors, min, max)
    : undefined;

  const optionsInRegion = useMemo(
    () =>
      scalesInRegion.map((scale) => ({
        scale,
        gradient: buildGradient(scale.colors, min, max),
      })),
    [scalesInRegion, min, max]
  );

  // The "Default" row must always preview the default scale itself, not
  // whatever's currently selected - selectedGradient tracks the latter and
  // only happens to match when value is already null.
  const defaultGradient = defaultOption
    ? optionsInRegion.find((o) => o.scale.color_scale_id === defaultOption.scaleId)?.gradient
    : undefined;

  return (
    <Select
      size="small"
      value={value === null ? NULL_VALUE : String(value)}
      onChange={(event) => {
        const raw = event.target.value;
        onChange(raw === NULL_VALUE ? null : Number(raw));
      }}
      sx={{ width: 220 }}
      renderValue={() => (
        <OptionRow
          label={value === null ? (defaultOption?.label ?? "") : value}
          gradient={selectedGradient}
          noWrap
        />
      )}
    >
      {defaultOption && (
        <MenuItem value={NULL_VALUE}>
          <OptionRow label={defaultOption.label} gradient={defaultGradient} />
        </MenuItem>
      )}
      {optionsInRegion.map(({ scale, gradient }) => (
        <MenuItem
          key={scale.color_scale_id}
          value={String(scale.color_scale_id)}
        >
          <OptionRow label={scale.color_scale_id} gradient={gradient} />
        </MenuItem>
      ))}
    </Select>
  );
}
